import { MAX_REQUESTS_TRACKED, requestTracking, useUserQuery } from '@alluradirect/react';
import * as Sentry from '@sentry/nextjs';
import { createDedupeString, errorMessageDedupe } from 'helpers/errorMessageDedupe/errorMessageDedupe';
import { useErrorsMutation } from 'hooks/api/useErrorMutation';
import React from 'react';
import { ErrorFallback } from './components/ErrorFallback';
import { hasReloaded } from './constants';

export interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

interface Props {
  handleReset?: () => void;
  Fallback?: any;
  children: React.ReactNode;
}

// The purpose of this error boundary is to catch errors on the client and allow the user to correct them
// If handleReset is not defined then the children will be remounted onReset
export const AlluraErrorBoundary = ({ handleReset = undefined, Fallback = undefined, children }: Props) => {
  const errorMutation = useErrorsMutation();
  const userQuery = useUserQuery();
  /** Safely get redux context
   * We cannot be certain that this error boundary is used in a location inside react-redux
   */

  const errorHandler = (error: Error, componentStack: string, _eventId: string) => {
    // Safely get redux state if its defined and try to get user info

    const userId = userQuery.data?.DATA.USER && userQuery.data.DATA.USER.ID ? userQuery.data.DATA.USER.ID : -1;
    // The USER_TYPE_ID is no longer valid as this error boundary is wrapping pages that are not part of the app.
    // If an error happens on the login page, there is no userTypeId.
    // We may be able to get the userTypeId from the router,

    const reloaded = window?.sessionStorage.getItem(hasReloaded);

    if (reloaded === null && error.name === 'ChunkLoadError') {
      window?.sessionStorage.setItem(hasReloaded, 'true');
      window?.location.reload();
      return;
    }

    const stack_trace = componentStack?.replace(/\n/g, '<br/>');
    const errorStackTrace = error?.stack?.replace(/\n/g, '<br/>');
    const formattedRequests = requestTracking?.getFormattedRequests();
    const windowLocation = window?.location?.href;
    const errorMessage = `Message: ${error?.message}<br/><br/>Stack Trace: ${errorStackTrace}<br/><br/>Error occured on URL: ${windowLocation}<br/><br/>Last ${MAX_REQUESTS_TRACKED} Requests:<br/><br/>${formattedRequests}`;
    const dedupeString = createDedupeString(error.message, windowLocation);

    if (!errorMessageDedupe.contains(dedupeString)) {
      errorMutation.mutate(
        { client_ip: 0, user_id: userId, user_type_id: -1, stack_trace, message: errorMessage },
        {
          onSuccess: () => {
            errorMessageDedupe.push(dedupeString);
          },
        },
      );
    }
  };

  // Changing the key value will cause the component to re-mount
  const [key, setKey] = React.useState(0);
  const onReset = () => {
    if (handleReset === undefined) {
      setKey(key => key + 1);
    } else {
      handleReset();
    }
  };

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => {
        if (Fallback) {
          return <Fallback error={error} resetErrorBoundary={resetError} />;
        }

        return <ErrorFallback error={error} resetErrorBoundary={resetError} />;
      }}
      onReset={onReset}
      onError={errorHandler}
    >
      <React.Fragment key={key}>{children}</React.Fragment>
    </Sentry.ErrorBoundary>
  );
};
